import React, { memo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { use100vh } from 'react-div-100vh';
import AuthForm from 'components/auth/tools/AuthForm';
import { HeaderPanel } from 'components/header/HeaderPanel';
import NiceModal from 'components/modals/NiceModal';
import SubscriptionModal from 'components/modals/SubscriptionModal';
import { useGetCurrentPromotionMode } from 'config/promotions';
import { LOGIN_MODE, RESEND_MODE, SIGNUP_MODE } from 'constants/auth';
import { isStipeProvider } from 'constants/provider';
import { useEmailVerificationCheck } from 'hooks/useCheckEmailValidation';
import useModalState from 'hooks/useModalState';
import useScrollToAnchor from 'hooks/useScrollToAnchor';
import FeedbackModal from 'modals/FeedbackModal';
import moment from 'moment';
import { useMappedState } from 'redux-react-hook';
import styled from 'styled-components';
import { formatDateToFullFormatWithSeconds, FULL_DATE_FORMAT_WITH_SECONDS, now } from 'utils/dates';
import { isBlank } from 'utils/storeUtils';

import CloudIcon from '../../../assets/cloud.svg?react';


const DialogUserTemplate = (props) => {
  const {
    stickyHeader = false,
    promotionBanner = false,
    analyticsData = {}
  } = props;

  const { currentUser, isSignedIn } = useMappedState(
    state => {
      const currentUserId = state.users.currentUserId;
      const currentUser = state.users.usersMap.get(currentUserId);
      const hasSubscription = !!currentUser?.hasSubscription;
      const isSignedIn = currentUserId !== undefined;
      return {
        currentUser: currentUser,
        hasSubscription: hasSubscription,
        isSignedIn: isSignedIn
      };
    });

  const isLoggedIn = !isBlank(currentUser);


  const [isAuthModalOpen, openAuthModal, closeAuthModal] = useModalState();
  const [isFeedbackModalOpen, openFeedbackModal, closeFeedbackModal] = useModalState();
  const [authMode, setAuthMode] = useState(LOGIN_MODE);
  const [subscriptionModalState, setSubscriptionModalState] = useState({
    isOpen: false,
    renderedForReason: undefined
  });

  useEmailVerificationCheck(openAuthModal, RESEND_MODE, setAuthMode);


  useScrollToAnchor();

  const promotionMode = useGetCurrentPromotionMode();

  const height = use100vh();

  const paymentProvider = currentUser?.subscription?.paymentProvider ?? '';
  const isStripeProvider = isStipeProvider(paymentProvider);
  const subscriptionEndDate = formatDateToFullFormatWithSeconds(currentUser?.subscription?.currentPeriodEndDate ?? '');

  const expireSubscriptionDate = moment(subscriptionEndDate, FULL_DATE_FORMAT_WITH_SECONDS);
  const nowTime = now();
  const duration = moment.duration(expireSubscriptionDate.diff(nowTime));

  const daysToExpireSubscription = Math.floor(duration.asDays());
  const isExpireLessWeekSub = daysToExpireSubscription <= 6;
  const isExpiredSub = nowTime.isAfter(expireSubscriptionDate);

  const subscriptionModalAnalyticsData = {
    rendered_for_reason: subscriptionModalState.renderedForReason,
    ...analyticsData
  };

  const handleSignIn = () => {
    setAuthMode(LOGIN_MODE);
    openAuthModal();
  };

  const handleSignUp = () => {
    setAuthMode(SIGNUP_MODE);
    openAuthModal();
  };

  const handleCloseSubscriptionModal = () => {
    setSubscriptionModalState({
      isOpen: false,
      renderedForReason: undefined
    });
  };

  const handlePremiumButtonClick = () => {
    if (isSignedIn) {
      setSubscriptionModalState({
        isOpen: true,
        renderedForReason: 'header_premium_click'
      });
      return;
    }
    handleSignIn();
  };

  const needToShowStripeOfferBanner = isStripeProvider && (isExpireLessWeekSub || isExpiredSub) && currentUser?.paymentProvider !== 'stripe4';
  const isShowPromotionBanner = promotionBanner && needToShowStripeOfferBanner;

  return (
    <Wrapper style={{ maxHeight: isMobile ? height : '' }}>
      {!isMobile && (
        <HeaderPanel
          isSticky={stickyHeader}
          isPromotionBanner={isShowPromotionBanner}
          promotionMode={promotionMode}
          isLoggedIn={isLoggedIn}
          onSignIn={handleSignIn}
          onSignUp={handleSignUp}
          onPremiumClick={handlePremiumButtonClick}
          onContactClick={openFeedbackModal}
        />
      )}
      {isShowPromotionBanner && <div style={{ height: '60px' }} />}

      <InnerWrapper
        className={'max-w-screen-2xl px-2 md:max-w-screen-xl md:px-6 lg:px-2'}
        style={{ height: isMobile ? height : '' }}
      >
        <ContentPlaceholder className={'template_children'}>{props.children}</ContentPlaceholder>
      </InnerWrapper>
      {isFeedbackModalOpen && (<FeedbackModal isVisible onClose={closeFeedbackModal} />)}
      {isAuthModalOpen && (
        <NiceModal onClose={closeAuthModal}>
          <ModalInnerPlaceholder>
            <AuthForm onClose={closeAuthModal} mode={authMode} />
          </ModalInnerPlaceholder>
        </NiceModal>
      )}
      {subscriptionModalState.isOpen && (
        <SubscriptionModal
          analyticsData={subscriptionModalAnalyticsData}
          isOpen
          onClose={handleCloseSubscriptionModal}
        />
      )}
      {!isMobile && <FeedbackButton type='button' onClick={openFeedbackModal}><CloudImg />Feedback</FeedbackButton>}
    </Wrapper>
  );
};

export default memo(DialogUserTemplate);

const ModalInnerPlaceholder = styled.div`
  width: 380px;
  padding: 24px;
  box-sizing: border-box;
  @media (max-width: 400px) {
    width: 100%;
  }
`;


const ContentPlaceholder = styled.div`
  font-family: 'Unbounded', sans-serif;
  font-optical-sizing: auto;
`;

const Wrapper = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-height: 100vh;
  background: rgba(16, 13, 11, 1);
`;

const InnerWrapper = styled.div`
  margin: 0 auto;
  @media (max-width: 720px) {
    padding-left: 12px;
    padding-right: 12px;
  }
`;

const FeedbackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  height: 40px;
  position:fixed;
  top: 50%;
  left: -38px;
  padding: 8px 12px 8px 12px;
  border-radius: 12px;
  transform: rotate(-90deg);
  color: #606060;
  font-family: Nunito Sans;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;

  :hover {
    color: #FFFFFF;
    background: #1D1D1D;
  }
`;

const CloudImg = styled(CloudIcon)`
  width: 20px;
  height: 20px;
  transform: rotate(90deg);
`;
